import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import style from "css/sign.module.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { insertAllowPageInfo } from "redux/slices/allowPageSlice";
import { insertUserInfo } from "redux/slices/userSlice";
import { Cookies } from "react-cookie";
import { Button, ConfigProvider, Modal, Select } from "antd";
import axios from "axios";
import { Input, Checkbox } from "antd";
import OpenModal from "function/openModal";
import modalStyle from "css/modal.module.css";
import FindPassword from "components/account/findPassword";
import UpdatePassword from "components/account/updatePassword";
import { useTranslation } from "react-i18next";
import { ReactComponent as Logo } from "asset/menu/logo.svg";
import CryptoJS from "crypto-js";

/**
 * 파일명: signin.js
 * 작성자: 권도훈
 * 작성일자: 23.04.06
 * 설명: sign in page
 */
function Signin() {
  //state
  const [loading, setLoading] = useState(false);
  const [isCheck, setIsCheck] = useState(false);
  const [user, setUser] = useState({
    id: "",
    pw: "",
  });

  const { t, i18n } = useTranslation();
  const languageList = [
    { value: "ko-KR", label: t("LANGUAGE.ko") },
    { value: "en-US", label: t("LANGUAGE.en") },
  ];
  const [selectType, setSelectType] = useState(i18n.language);

  //navigate
  const navigate = useNavigate();

  //dispatch
  const dispatch = useDispatch();

  //cookies
  const cookies = new Cookies();

  //ref
  const pwRef = useRef();

  //selector
  const userPayload = useSelector((state) => state.user);

  //env
  const authNm = process.env.REACT_APP_AUTH_TOKEN;
  // const authTime = process.env.REACT_APP_AUTH_TOKEN_TIME;
  const refreshNm = process.env.REACT_APP_REFRESH_TOKEN;
  const refreshTime = process.env.REACT_APP_REFRESH_TOKEN_TIME;
  // const refreshLongTime = process.env.REACT_APP_REFRESH_TOKEN_LONG_TIME;
  const domain = process.env.REACT_APP_DOMAIN;

  const openUpdatePassword = useCallback(() => {
    Modal.info({
      icon: null,
      centered: true,
      title: (
        <p className={modalStyle.modal_title}>
          {t("LABEL.TITLE.RESET_PASSWORD")}
        </p>
      ),
      content: (
        <div className={modalStyle.modal_content}>
          <UpdatePassword openUpdatePassword={openUpdatePassword} />
        </div>
      ),
      autoFocusButton: null,
      okButtonProps: {
        style: {
          display: "none",
        },
      },
    });
  }, [t]);

  useLayoutEffect(() => {
    const email = new URL(window.location.href).searchParams.get("email");
    const authcode = new URL(window.location.href).searchParams.get("authcode");

    const isEmail = email !== undefined && email !== null && email !== "";
    const isAuthcode =
      authcode !== undefined && authcode !== null && authcode !== "";

    if (isEmail && isAuthcode) {
      const data = {
        email: email,
        authcode: authcode,
      };

      axios
        .post("/user/password/authcode", data)
        .then((res) => {
          if (res.status === 200) {
            openUpdatePassword();
          }
        })
        .catch((err) => {
          const props = {
            type: "info",
            title: t("LABEL.TITLE.MESSAGE"),
            content: t("MESSAGE." + err.response.data),
            okText: t("common.button.ok"),
            img:
              process.env.REACT_APP_CLIENT_URL +
              "img/common/modal_icon_green.png",
            btnEvent: () =>
              (window.location.href = process.env.REACT_APP_CLIENT_URL),
          };
          OpenModal(props);
        });
    }
  }, [openUpdatePassword, t]);

  useEffect(() => {
    if (userPayload.name !== "") {
      navigate(process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule");
    }
  }, [navigate, userPayload.name]);

  useEffect(() => {
    return () => setLoading(false);
  }, []);

  const loginHandler = () => {
    setLoading(true);

    // const data = {
    //   MEMBER_ID: user.id,
    //   MEMBER_PW: user.pw,
    // };
    const data = {
      MEMBER_ID: CryptoJS.AES.encrypt(
        user.id,
        CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
        {
          iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
          padding: CryptoJS.pad.Pkcs7,
          mode: CryptoJS.mode.CBC,
        }
      ).toString(),
      MEMBER_PW: CryptoJS.AES.encrypt(
        user.pw,
        CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
        {
          iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
          padding: CryptoJS.pad.Pkcs7,
          mode: CryptoJS.mode.CBC,
        }
      ).toString(),
    };

    axios
      .post("/user/login", data)
      .then((res) => {
        if (res.status === 200) {
          const memeberInfo = res.data.MEMBER_INFO;

          cookies.set(authNm, memeberInfo.AUTH_TOKEN, {
            path: "/",
            // maxAge: isCheck ? authTime : null,
            domain: domain,
          });

          cookies.set(refreshNm, memeberInfo.REFRESH_TOKEN, {
            path: "/",
            maxAge: isCheck ? refreshTime : null,
            domain: domain,
          });

          dispatch(
            insertUserInfo({
              name: memeberInfo.MEMBER_NM,
              id: memeberInfo.MEMBER_ID,
              zoomId: memeberInfo.ZOOM_ID,
            })
          );

          localStorage.setItem("noticeAlert", "true"); // 메인페이지 공지사항 표출

          const allowPages = res.data.ALLOW_PAGES;

          dispatch(insertAllowPageInfo({ pages: allowPages }));
          navigate(process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule");
        }
      })
      .catch((err) => {
        setLoading(false);
        const props = {
          type: "info",
          title: t("LABEL.TITLE.MESSAGE"),
          content: t("MESSAGE." + err.response.data),
          okText: t("common.button.ok"),
          img:
            process.env.REACT_APP_CLIENT_URL +
            "img/common/modal_icon_green.png",
          btnEvent: () => setLoading(false),
        };
        OpenModal(props);
      });
  };

  const passwordHandler = () => {
    Modal.info({
      icon: null,
      centered: true,
      title: (
        <p className={modalStyle.modal_title}>
          {t("LABEL.TITLE.CHANGE_PASSWORD")}
        </p>
      ),
      content: (
        <div className={modalStyle.modal_content}>
          <FindPassword
            sendMailSuccess={sendMailSuccess}
            sendMailFail={sendMailFail}
          />
        </div>
      ),
      autoFocusButton: null,
      okButtonProps: {
        style: {
          display: "none",
        },
      },
    });
  };

  const sendMailSuccess = () => {
    const props = {
      type: "info",
      title: t("LABEL.TITLE.MESSAGE"),
      content: t("MESSAGE.MAIL_SEND_SUCCESS"),
      okText: t("common.button.ok"),
      img: process.env.REACT_APP_CLIENT_URL + "img/common/modal_icon_green.png",
      btnEvent: () => {},
    };
    OpenModal(props);
  };

  const sendMailFail = (err) => {
    const props = {
      type: "info",
      title: t("LABEL.TITLE.MESSAGE"),
      content: t("user." + err.response.data),
      okText: t("common.button.ok"),
      img: process.env.REACT_APP_CLIENT_URL + "img/common/modal_icon_green.png",
      btnEvent: () => passwordHandler(),
    };
    OpenModal(props);
  };

  return (
    <div className={style.wrapper}>
      <div className={style.container}>
        <section className={style.section}>
          <ConfigProvider
            theme={{
              components: {
                Select: {
                  // select area
                  colorBgContainer: "transparent",
                  colorText: "white",
                  // dropdown area
                  colorBgElevated: "transparent",
                  controlItemBgActive: "black",
                  controlItemBgActiveHover: "black",
                  controlItemBgHover: "black",
                  // colorIcon: "white"
                },
              },
            }}
          >
            <div className="section_select">
              <Select
                options={languageList}
                value={selectType}
                onChange={(value) => {
                  setSelectType(value);
                  i18n.changeLanguage(value);
                }}
                className="select-box"
              />
            </div>
          </ConfigProvider>

          <div>
            <Logo className={style.logo} />
            <p className={style.title}>Hybrid working system</p>
          </div>

          <div>
            <div className={style.item}>
              <p className={style.item_title}>User ID</p>
              <ConfigProvider
                theme={{
                  components: {
                    Input: {
                      colorBgContainer: "transparent",
                      colorTextPlaceholder: "white",
                      colorText: "white",
                    },
                  },
                }}
              >
                <Input
                  className={style.item_input}
                  placeholder={t("PLACEHOLDER.INPUT.ENTER_ID")}
                  // disabled={loading}
                  onChange={(e) => setUser({ ...user, id: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      pwRef.current.focus();
                    }
                  }}
                />
              </ConfigProvider>
            </div>

            <div className={style.item}>
              <p className={style.item_title}>Password</p>
              <ConfigProvider
                theme={{
                  components: {
                    Input: {
                      colorBgContainer: "transparent",
                      colorTextPlaceholder: "white",
                      colorText: "white",
                      colorIcon: "white",
                      colorIconHover: "white",
                    },
                  },
                }}
              >
                <Input.Password
                  ref={pwRef}
                  className={style.item_input}
                  placeholder={t("PLACEHOLDER.INPUT.ENTER_PW")}
                  // disabled={loading}
                  onChange={(e) => setUser({ ...user, pw: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      loginHandler();
                    }
                  }}
                />
              </ConfigProvider>
            </div>

            <div className={style.option_item}>
              <ConfigProvider
                theme={{
                  components: {
                    Checkbox: {
                      colorBgContainer: "transparent",
                    },
                  },
                }}
              >
                <Checkbox
                  className={style.chkbox}
                  checked={isCheck}
                  onClick={() => setIsCheck(!isCheck)}
                >
                  {t("BTN.KEEP_ME_LOGGED_IN")}
                </Checkbox>
              </ConfigProvider>

              <p
                className={`${style.password} cursor`}
                onClick={() => passwordHandler()}
              >
                {t("BTN.FORGOT_PW")}
              </p>
            </div>

            <Button
              className={style.button}
              type="primary"
              loading={loading}
              onClick={() => loginHandler()}
              style={{
                fontFamily: "SpoqaHanSansNeo",
                fontWeight: "600",
                fontSize: "18px",
              }}
            >
              {t("BTN.LOGIN")}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Signin;
